/**
 * AcuityActivityFeed - Activity items as conversational Acuity observations
 * 
 * Activities appear directly in the chat as Acuity's proactive messages,
 * pausing when the user is asking questions and Acuity is responding.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

// Activity templates with Acuity-style messages and actions
const activityTemplates = [
  {
    type: 'update',
    user: 'Michael Chen',
    action: 'updated',
    target: 'T4NG proposal timeline',
    acuityMessage: "Just noticed — {user} updated the {target}.",
    suggestion: "Would you like me to summarize the changes?",
    actions: [
      { label: 'View Changes', action: 'navigate', path: '/projects' },
      { label: 'Summarize', action: 'summarize', context: 'proposal_timeline' }
    ]
  },
  {
    type: 'match',
    user: 'System',
    action: 'found',
    target: 'new opportunity with 95% relevance',
    acuityMessage: "Great news! I found a {target} to your current projects.",
    suggestion: "This could be a good fit for your team.",
    actions: [
      { label: 'View Opportunity', action: 'navigate', path: '/opportunities' },
      { label: 'Add to Watchlist', action: 'watchlist' }
    ]
  },
  {
    type: 'complete',
    user: 'System',
    action: 'completed',
    target: 'compliance check',
    acuityMessage: "The automated {target} just finished.",
    suggestion: "All items passed. Your documents are ready for submission.",
    actions: [
      { label: 'View Report', action: 'navigate', path: '/compliance' }
    ]
  },
  {
    type: 'comment',
    user: 'Lisa Rodriguez',
    action: 'commented on',
    target: 'EHR Integration RFP',
    acuityMessage: "Heads up — {user} left a comment on the {target}.",
    suggestion: "It looks like they have a question about Section 3.2.",
    actions: [
      { label: 'View Comment', action: 'navigate', path: '/va-library' },
      { label: 'Reply', action: 'reply' }
    ]
  },
  {
    type: 'upload',
    user: 'James Park',
    action: 'uploaded',
    target: 'VISN 8 budget analysis',
    acuityMessage: "{user} just uploaded the {target}.",
    suggestion: "I can analyze this document for key insights if you'd like.",
    actions: [
      { label: 'View Document', action: 'navigate', path: '/va-library' },
      { label: 'Analyze', action: 'analyze', context: 'budget' }
    ]
  },
  {
    type: 'approval',
    user: 'Emily Watson',
    action: 'approved',
    target: 'vendor qualification for SecureVet Inc.',
    acuityMessage: "{user} approved the {target}.",
    suggestion: "You can now proceed with the contract award process.",
    actions: [
      { label: 'View Projects', action: 'navigate', path: '/projects' },
      { label: 'View Details', action: 'navigate', path: '/vendors' }
    ]
  },
  {
    type: 'alert',
    user: 'System',
    action: 'detected',
    target: 'deadline in 3 days',
    acuityMessage: "Heads up! There's a {target} for the VA Mobile Health RFP.",
    suggestion: "You might want to review the submission checklist.",
    actions: [
      { label: 'View Deadline', action: 'navigate', path: '/calendar' },
      { label: 'Set Reminder', action: 'reminder' }
    ],
    priority: 'high'
  },
  {
    type: 'meeting',
    user: 'Sarah Miller',
    action: 'scheduled',
    target: 'stakeholder review for tomorrow at 2pm',
    acuityMessage: "{user} scheduled a {target}.",
    suggestion: "I can prepare a briefing document for you.",
    actions: [
      { label: 'View Meeting', action: 'navigate', path: '/calendar' },
      { label: 'Prep Briefing', action: 'briefing' }
    ]
  },
  {
    type: 'score',
    user: 'System',
    action: 'updated',
    target: 'compliance score to 96%',
    acuityMessage: "Your {target} — that's up 3 points from last week!",
    suggestion: null,
    actions: [
      { label: 'View Details', action: 'navigate', path: '/analytics' }
    ]
  },
  {
    type: 'sync',
    user: 'System',
    action: 'synced',
    target: 'SAM.gov data',
    acuityMessage: "I just finished syncing {target}.",
    suggestion: "2 vendor profiles were updated with new certifications.",
    actions: [
      { label: 'View Updates', action: 'navigate', path: '/vendors' }
    ]
  },
];

// Format message with variables
const formatMessage = (template, data) => {
  return template
    .replace('{user}', data.user)
    .replace('{target}', data.target);
};

// Get relative time string
const getRelativeTime = (timestamp) => {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 4) return `${hours}h ago`;
  return 'earlier';
};

/**
 * Hook to manage live activity feed that integrates with chat
 * 
 * @param {boolean} isLoading - Whether Acuity is currently processing/responding
 * @param {boolean} liveFeedEnabled - Whether live feed is enabled in settings
 * @param {function} onSendMessage - Callback to send a message in chat
 * @returns {object} - { activityMessages, handleActivityAction, dismissActivity }
 */
export function useLiveActivityFeed(isLoading, liveFeedEnabled, onSendMessage) {
  const navigate = useNavigate();
  const [activityMessages, setActivityMessages] = useState([]);
  const isPausedRef = useRef(false);
  const pendingActivityRef = useRef(null);

  // Pause activity feed when Acuity is loading/responding
  useEffect(() => {
    isPausedRef.current = isLoading;
    
    // When loading finishes, check for pending activity
    if (!isLoading && pendingActivityRef.current && liveFeedEnabled) {
      const pending = pendingActivityRef.current;
      pendingActivityRef.current = null;
      
      // Add pending activity after a short delay
      setTimeout(() => {
        if (!isPausedRef.current) {
          setActivityMessages(prev => [...prev, pending]);
        }
      }, 2000);
    }
  }, [isLoading, liveFeedEnabled]);

  // Generate new activity periodically
  useEffect(() => {
    if (!liveFeedEnabled) return;

    const generateActivity = () => {
      const randomTemplate = activityTemplates[Math.floor(Math.random() * activityTemplates.length)];
      const newActivity = {
        ...randomTemplate,
        id: `activity-${Date.now()}`,
        role: 'activity',
        timestamp: Date.now(),
        content: formatMessage(randomTemplate.acuityMessage, randomTemplate),
      };

      // If paused (Acuity is responding), queue the activity
      if (isPausedRef.current) {
        pendingActivityRef.current = newActivity;
      } else {
        setActivityMessages(prev => [...prev, newActivity]);
      }
    };

    // Initial activity after 5 seconds
    const initialTimer = setTimeout(generateActivity, 5000);

    // Then periodically (15-25 seconds)
    const interval = setInterval(() => {
      generateActivity();
    }, 15000 + Math.random() * 10000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [liveFeedEnabled]);

  // Handle action button clicks
  const handleActivityAction = useCallback((action, activity) => {
    switch (action.action) {
      case 'navigate':
        navigate(action.path);
        break;
      case 'summarize':
        onSendMessage?.(`Summarize the recent changes to ${activity.target}`);
        break;
      case 'analyze':
        onSendMessage?.(`Analyze the ${activity.target} and highlight key insights`);
        break;
      case 'briefing':
        onSendMessage?.(`Prepare a briefing document for the ${activity.target}`);
        break;
      case 'watchlist':
        logger.log('Adding to watchlist:', activity.target);
        break;
      case 'reminder':
        logger.log('Setting reminder for:', activity.target);
        break;
      case 'reply':
        onSendMessage?.(`Help me draft a reply to ${activity.user}'s comment on ${activity.target}`);
        break;
      default:
        break;
    }
  }, [navigate, onSendMessage]);

  // Dismiss an activity
  const dismissActivity = useCallback((activityId) => {
    setActivityMessages(prev => prev.filter(a => a.id !== activityId));
  }, []);

  // Clear all activities
  const clearActivities = useCallback(() => {
    setActivityMessages([]);
  }, []);

  return {
    activityMessages,
    handleActivityAction,
    dismissActivity,
    clearActivities
  };
}

/**
 * Activity message component to render in chat - Compact version
 */
export function ActivityMessage({ activity, onAction, onDismiss, darkMode, assistantName }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  useEffect(() => {
    // Fade in animation
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsDismissing(true);
    setTimeout(() => onDismiss(activity.id), 300);
  };

  const priorityBorder = activity.priority === 'high' 
    ? 'border-l-amber-400'
    : 'border-l-violet-400';

  return (
    <div 
      className={`flex justify-start transition-all duration-300 ${
        isVisible && !isDismissing ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <div 
        className={`max-w-[80%] rounded-xl px-2.5 py-2 rounded-bl-sm border-l-2 ${priorityBorder} ${
          darkMode ? 'bg-gray-700/60 text-gray-100' : 'bg-white text-gray-800 shadow-sm'
        }`}
      >
        {/* Activity indicator - compact */}
        <div className="flex items-center gap-1.5 mb-1">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
          </span>
          <span className={`text-[9px] font-medium uppercase tracking-wide ${
            darkMode ? 'text-violet-400' : 'text-violet-600'
          }`}>
            {assistantName} noticed
          </span>
          <button
            onClick={handleDismiss}
            className={`ml-auto p-0.5 rounded transition-colors ${
              darkMode ? 'hover:bg-gray-600 text-gray-500' : 'hover:bg-gray-100 text-gray-400'
            }`}
            title="Dismiss"
          >
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Message content - slightly smaller */}
        <p className="text-[13px] leading-snug">{activity.content}</p>

        {/* Suggestion - compact */}
        {activity.suggestion && (
          <p className={`text-[11px] mt-1 leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {activity.suggestion}
          </p>
        )}

        {/* Action buttons - smaller */}
        {activity.actions && activity.actions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {activity.actions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => onAction(action, activity)}
                className={`
                  px-2 py-0.5 text-[10px] font-medium rounded transition-all
                  ${idx === 0 
                    ? 'bg-violet-600 hover:bg-violet-700 text-white' 
                    : (darkMode 
                        ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700')
                  }
                `}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp - smaller */}
        <p className={`text-[9px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {getRelativeTime(activity.timestamp)}
        </p>
      </div>
    </div>
  );
}

// Default export for backward compatibility (though we'll remove the old split view)
export default function AcuityActivityFeed() {
  // This is now deprecated - activity is managed via the hook
  return null;
}
